import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OAuth2TokenResponse } from '../../shared/models/api.models';

/**
 * OAuth2 Service for Microsoft Azure AD with PKCE
 * Implements Authorization Code Flow with PKCE as specified in Postman collection
 */
@Injectable({
  providedIn: 'root',
})
export class OAuth2Service {
  private http = inject(HttpClient);
  private oauthConfig = environment.oauth;

  // PKCE state
  private codeVerifier: string | null = null;
  private state: string | null = null;

  /**
   * Get the appropriate redirect URI based on current environment
   * Dynamically determines the correct redirect URI for each environment
   */
  private getRedirectUri(): string {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    // Local development with OAuth bridge
    if (hostname === 'localhost') {
      return 'http://localhost:4300/index.html';
    }

    // Azure Static Web Apps (production, staging, or PR previews)
    if (hostname.includes('azurestaticapps.net')) {
      return `${origin}/auth/callback`;
    }

    // Custom domain or other production environments
    if (environment.production) {
      return `${origin}/auth/callback`;
    }

    // Fallback to configured redirect URI
    return this.oauthConfig.redirectUri || `${origin}/auth/callback`;
  }

  /**
   * Start OAuth2 authorization flow
   * Redirects user to Microsoft Azure AD authorization endpoint
   */
  authorize(): void {
    // Store the current origin URL for redirect back after OAuth
    sessionStorage.setItem('oauth_origin', window.location.origin);

    // Generate PKCE parameters
    this.generatePKCEParams().then(({ codeVerifier, codeChallenge, state }) => {
      // Store PKCE parameters for token exchange
      this.storeAuthParams(codeVerifier, state);

      // Build authorization URL
      const authUrl = this.buildAuthorizationUrl(codeChallenge, state);

      // Redirect to authorization endpoint
      window.location.href = authUrl;
    });
  }

  /**
   * Handle OAuth2 callback
   * Exchanges authorization code for access token
   */
  handleCallback(code: string, state: string): Observable<OAuth2TokenResponse> {
    // Verify state parameter
    if (state !== this.getStoredState()) {
      throw new Error('Invalid state parameter');
    }

    const codeVerifier = this.getStoredCodeVerifier();
    if (!codeVerifier) {
      throw new Error('No code verifier found');
    }

    // Exchange code for token
    return this.exchangeCodeForToken(code, codeVerifier);
  }

  /**
   * Refresh the OAuth2 access token using a refresh token
   * Microsoft refresh tokens are one-time use; a new refresh token is returned on each refresh
   * @param refreshToken The current refresh token
   * @returns Observable with new token response including new access_token and refresh_token
   */
  refreshAccessToken(refreshToken: string): Observable<OAuth2TokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', this.oauthConfig.clientId)
      .set('refresh_token', refreshToken)
      .set('scope', this.oauthConfig.scope);

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    return this.http.post<OAuth2TokenResponse>(this.oauthConfig.tokenUrl, body.toString(), {
      headers,
    });
  }

  /**
   * Build authorization URL with PKCE parameters
   */
  private buildAuthorizationUrl(codeChallenge: string, state: string): string {
    const params = new URLSearchParams({
      client_id: this.oauthConfig.clientId,
      response_type: this.oauthConfig.responseType,
      redirect_uri: this.getRedirectUri(), // Use dynamic redirect URI
      scope: this.oauthConfig.scope,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: this.oauthConfig.codeChallenge, // S256
      response_mode: 'query',
    });

    return `${this.oauthConfig.authorizeUrl}?${params.toString()}`;
  }

  /**
   * Exchange authorization code for access token
   */
  private exchangeCodeForToken(
    code: string,
    codeVerifier: string
  ): Observable<OAuth2TokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', this.oauthConfig.clientId)
      .set('code', code)
      .set('redirect_uri', this.getRedirectUri()) // Use dynamic redirect URI
      .set('code_verifier', codeVerifier);

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    return this.http.post<OAuth2TokenResponse>(this.oauthConfig.tokenUrl, body.toString(), {
      headers,
    });
  }

  /**
   * Generate PKCE parameters (code verifier, code challenge, state)
   */
  private async generatePKCEParams(): Promise<{
    codeVerifier: string;
    codeChallenge: string;
    state: string;
  }> {
    // Generate code verifier (43-128 characters)
    const codeVerifier = this.generateRandomString(128);

    // Generate code challenge (SHA256 hash of code verifier, base64url encoded)
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // Generate state parameter
    const state = this.generateRandomString(32);

    return { codeVerifier, codeChallenge, state };
  }

  /**
   * Generate random string for PKCE parameters
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const values = crypto.getRandomValues(new Uint8Array(length));

    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }

    return result;
  }

  /**
   * Generate code challenge from code verifier
   */
  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    // Encode code verifier as UTF-8
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);

    // Hash with SHA256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to base64url
    return this.base64UrlEncode(hashBuffer);
  }

  /**
   * Convert ArrayBuffer to base64url string
   */
  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Store PKCE parameters in session storage
   */
  private storeAuthParams(codeVerifier: string, state: string): void {
    sessionStorage.setItem('oauth_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);
  }

  /**
   * Get stored code verifier
   */
  private getStoredCodeVerifier(): string | null {
    return sessionStorage.getItem('oauth_code_verifier');
  }

  /**
   * Get stored state
   */
  private getStoredState(): string | null {
    return sessionStorage.getItem('oauth_state');
  }

  /**
   * Clear stored OAuth parameters
   */
  clearAuthParams(): void {
    sessionStorage.removeItem('oauth_code_verifier');
    sessionStorage.removeItem('oauth_state');
  }

  /**
   * Check if current URL contains OAuth callback parameters
   */
  isOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') && urlParams.has('state');
  }

  /**
   * Extract OAuth callback parameters from URL
   */
  getCallbackParams(): { code: string; state: string } | null {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      return { code, state };
    }

    return null;
  }
}
